// import { Lender } from 'apps\lenderhub\src\app\models/lenders.ts';
import { Injectable, inject } from '@angular/core';
import {
  GridStateModel, LenderManagementViewModel,
  LenderName,
  LenderProductsConfig,
  LenderProductsConfigWithValidation,
  OtherLenderType,
  ProductUpload, SimpleGridResponseViewModel,
} from '@shared/models';
import { LendingType } from '@shared/models/lending-type';
import { LenderHubDataService } from '@shared/services';
import { ClubHubDataService } from '@shared/services/clubhub-data.service';
import { Observable } from 'rxjs';
import { Lender } from './../models';

@Injectable({
  providedIn: 'root',
})
export class LenderService {
  private readonly clubHubDataService = inject(ClubHubDataService);
  private readonly lenderHubDataService = inject(LenderHubDataService);

  public downloadDocument(lenderNameId: number, sheetId: number) {
    return this.clubHubDataService.getFile(`Lender/DownloadSheet/${lenderNameId}/${sheetId}`);
  }

  public getLenders(lendingType: LendingType) {
    return this.clubHubDataService.get<Lender[]>(`Lender/GetLenders/${lendingType.code}`);
  }

  public getUnarchivedLenders(lendingType: LendingType) {
    return this.clubHubDataService.get<Lender[]>(`Lender/GetUnarchivedLenders/${lendingType.code}`);
  }

  /**
   * Every unarchived lender across all lending types. The API treats a missing lending type code as
   * "all", and filters to lenders whose LenderName is onboarded.
   */
  public getAllUnarchivedLenders() {
    return this.clubHubDataService.get<Lender[]>('Lender/GetUnarchivedLenders/');
  }

  public getAllManagementLenders() {
    return this.clubHubDataService.get<LenderManagementViewModel[]>('Lender/AllManagementLenders');
  }

  public getUnarchivedLendersNames(lendingType?: LendingType) {
    return this.clubHubDataService.get<LenderName[]>(`Lender/GetUnarchivedLenderNames/${lendingType?.code ?? ''}`);
  }

  public getLender(lenderId: number) {
    return this.clubHubDataService.get<Lender>(`Lender/${lenderId}`);
  }

  public getLenderProductsConfig(lenderId: number) {
    return this.clubHubDataService.get<LenderProductsConfigWithValidation>(`Lender/ProductsConfig/${lenderId}`);
  }

  public getLenderHandlerAvailability(lenderId: number) {
    return this.lenderHubDataService.get<{ bespoke: boolean; api: boolean }>(`LenderName/HandlerAvailability/${lenderId}`);
  }

  public updateLenderConfig(lenderId: number, lender: LenderProductsConfig) {
    return this.clubHubDataService.put<Lender>(`Lender/ProductsConfig/${lenderId}`, lender, null, {
      // Manually handle the errors, since the endpoint will throw a validation error if the details are invalid.
      // Do not want to show an error toast in this case
      showErrorToast: false,
    });
  }

  public updateLender(lenderId: number, lender: Lender) {
    return this.clubHubDataService.put<Lender>(`Lender/Update/${lenderId}`, lender);
  }

  public getPartialFlow(id: number) {
    return this.clubHubDataService.get(`Answer/Children/${id}`);
  }

  public updateLenderOutcome(changes: unknown) {
    return this.clubHubDataService.post('Lender/UpdateLenderOutcomes', changes);
  }

  public uploadLogo(lenderId: number, fileType: string, logo: unknown) {
    fileType = fileType ? fileType.replace('image/', '').replace('+xml', '') : 'jpeg';
    return this.clubHubDataService.postForText(`Lender/UploadLogo/${lenderId}/${fileType}`, logo);
  }

  public removeLogo(lenderId: number) {
    return this.clubHubDataService.delete(`Lender/RemoveLogo/${lenderId}`);
  }

  public getLenderDomains(lenderNameId: number): Observable<string[]> {
    return this.clubHubDataService.get<string[]>(`LenderDomain/${lenderNameId}`);
  }

  public addLenderDomain(lenderNameId: number, domain: string) {
    return this.clubHubDataService.post('LenderDomain', { lenderNameId, domain });
  }

  public deleteLenderDomain(lenderNameId: number, domain: string) {
    return this.clubHubDataService.delete(`LenderDomain/${lenderNameId}/${domain}`);
  }

  public getOtherLenderTypes(lenderId: number) {
    return this.clubHubDataService.get<OtherLenderType[]>(`Lender/OtherLenderTypes/${lenderId}`);
  }

  public addLenderName(lender: LenderManagementViewModel) {
    return this.clubHubDataService.post('Lender/LenderName', lender);
  }

  public updateLenderName(lender: LenderManagementViewModel) {
    return this.clubHubDataService.put('Lender/LenderName', lender);
  }

  public getProductSheetUploads(lenderId: number, gridState: GridStateModel) {
    return this.clubHubDataService.post<SimpleGridResponseViewModel<ProductUpload>>(`Lender/ProductUploads/${lenderId}`, gridState, {
      loading: false,
    });
  }

  public retrieveProductsViaApi(lenderId: number) {
    return this.lenderHubDataService.post<never>(`Product/RetrieveApiProducts/${lenderId}`, null, null, {
      showErrorToast: false,
    });
  }

  public getLenderDetails(lenderId: number) {
    return this.clubHubDataService.get<Lender>(`LenderDetails/${lenderId}`);
  }
}
